// src/features/merchantConfig/hooks/usePatchMerchantConfig.js
//
// TanStack Query mutation hook for the PATCH endpoint. Returns the standard
// useMutation API plus a clean cache integration:
//   * On success the returned envelope replaces the GET cache via setQueryData
//     (no refetch round-trip).
//   * On failure the GET cache is left untouched.
//
// Retry policy mirrors useMerchantConfig: 4xx errors (validation, not-found)
// are not retried; only network / 5xx faults are. Default v5 mutation behavior
// is `retry: 0`, which is what we want for the validation-heavy 4xx codes
// (PARAMETER_VALUE_*, INVALID_PARAMETER_VALUE, RECORD_NOT_FOUND, VALUE_DOES_NOT_MATCH).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchMerchantConfig } from "../api/merchantConfigClient";
import { merchantConfigQueryKey } from "./useMerchantConfig";

/**
 * Compose the mutation key. Exposed so consumers can observe the pending
 * state across components if needed.
 *
 * @param {string} merchantId
 * @param {string} configKey
 */
export const merchantConfigPatchMutationKey = (merchantId, configKey) => [
  "merchantConfig",
  "patch",
  merchantId,
  configKey,
];

/**
 * @param {{
 *   merchantId: string | null | undefined,
 *   configKey: string | null | undefined,
 *   onSuccess?: (envelope: import('../api/merchantConfigClient').MerchantConfigEnvelope) => void,
 *   onError?: (err: import('../api/merchantConfigClient').MerchantConfigError) => void,
 * }} options
 */
export function usePatchMerchantConfig({
  merchantId,
  configKey,
  onSuccess,
  onError,
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: merchantConfigPatchMutationKey(merchantId, configKey),
    mutationFn: async (body) => {
      // body is the inner value block (changed-only subset) — caller is
      // responsible for diffing draft vs original before this is invoked.
      return patchMerchantConfig({ merchantId, configKey, body });
    },
    onSuccess: (updatedEnvelope) => {
      if (merchantId && configKey) {
        // Write the freshest envelope into the GET cache so subsequent reads
        // resolve instantly, no refetch needed. The server has already done
        // `@CachePut` so the next GET would also be correct — this just
        // saves the round-trip.
        queryClient.setQueryData(
          merchantConfigQueryKey(merchantId, configKey),
          updatedEnvelope
        );
      }
      if (onSuccess) onSuccess(updatedEnvelope);
    },
    onError: (err) => {
      if (onError) onError(err);
    },
    retry: (failureCount, error) => {
      // No retry on 4xx — these are deterministic validation outcomes.
      if (error?.status && error.status >= 400 && error.status < 500) return false;
      // No retry on "not provisioned" — the doc literally doesn't exist.
      if (error?.isNotFound) return false;
      // Limited retry on 5xx / network — PATCH is idempotent at the field
      // level (Jackson readerForUpdating semantics), so retry is safe.
      return failureCount < 1;
    },
    retryDelay: (attemptIndex) => Math.min(1_000 * 2 ** attemptIndex, 4_000),
  });
}
